<template>
  <div class="push-page">
    <MarketingToolHeader
      eyebrow="Kommunikation"
      title="Push-Nachricht"
      description="Ein kurzer Hinweis, direkt auf dem Smartphone. Wähle ein Studio und verfasse deine Nachricht."
      icon="mdi-cellphone-message" />

    <div class="push-layout">
      <v-card dark flat class="push-panel compose-panel">
        <section aria-labelledby="push-audience-heading">
          <div class="section-heading">
            <span class="step-number">01</span>
            <div><h2 id="push-audience-heading">Empfänger auswählen</h2><p>Welches Studio möchtest du erreichen?</p></div>
          </div>
          <v-select
            v-model="selectedStudio"
            :items="studios"
            :disabled="isSending"
            item-value="code"
            item-text="name"
            label="Studio"
            placeholder="Studio auswählen"
            outlined
            hide-details
            prepend-inner-icon="mdi-storefront-outline"
            class="push-field" />
          <div class="audience-status" :class="{ 'audience-status--empty': selectedStudio && !loadingCount && recipientCount === 0 }" role="status" aria-live="polite">
            <v-progress-circular v-if="loadingCount" indeterminate size="20" width="2" color="#58d388" />
            <v-icon v-else size="20" aria-hidden="true">{{ selectedStudio && recipientCount > 0 ? 'mdi-account-check-outline' : 'mdi-information-outline' }}</v-icon>
            <span v-if="loadingCount">Empfänger werden ermittelt …</span>
            <span v-else-if="!selectedStudio">Wähle ein Studio, um die Empfängerzahl zu sehen.</span>
            <span v-else-if="recipientCount > 0"><strong>{{ recipientCount }}</strong> {{ recipientCount === 1 ? 'Profil mit registriertem Gerät' : 'Profile mit registriertem Gerät' }}</span>
            <span v-else>Für dieses Studio sind aktuell keine Geräte registriert.</span>
          </div>
        </section>

        <section class="message-section" aria-labelledby="push-message-heading">
          <div class="section-heading">
            <span class="step-number">02</span>
            <div><h2 id="push-message-heading">Nachricht verfassen</h2><p>Kurz, verständlich und mit dem Wichtigsten zuerst.</p></div>
          </div>
          <v-text-field
            v-model="title"
            :disabled="isSending"
            label="Titel"
            placeholder="Zum Beispiel: Heute länger für dich geöffnet"
            outlined
            hide-details
            class="push-field" />
          <div class="field-meta"><span>Empfohlen: bis zu 50 Zeichen</span><span>{{ title.length }} Zeichen</span></div>
          <v-textarea
            v-model="body"
            :disabled="isSending"
            label="Nachricht"
            placeholder="Was sollen die Mitglieder wissen?"
            outlined
            hide-details
            auto-grow
            rows="4"
            class="push-field message-field" />
          <div class="field-meta"><span>Empfohlen: bis zu 150 Zeichen</span><span>{{ body.length }} Zeichen</span></div>
        </section>

        <div class="compose-actions">
          <p>Im nächsten Schritt prüfst du die Nachricht vor dem Versand.</p>
          <v-btn
            color="#007a35"
            depressed
            large
            class="push-action"
            :disabled="!isFormValid || loadingCount || recipientCount === 0 || isSending"
            @click="confirmDialog = true">
            Nachricht prüfen<v-icon right size="20">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-card>

      <aside class="preview-column" aria-labelledby="push-preview-heading">
        <v-card dark flat class="push-panel preview-panel">
          <div class="preview-heading"><h2 id="push-preview-heading">Vorschau</h2><span class="preview-badge">Live</span></div>
          <p class="preview-intro">So könnte deine Nachricht auf dem Smartphone aussehen.</p>
          <div class="preview-stage">
            <div class="preview-device-label"><v-icon size="18" aria-hidden="true">mdi-cellphone</v-icon>Push-Mitteilung</div>
            <div class="notification-preview">
              <div class="notification-meta">
                <span class="notification-icon"><v-icon size="18" color="white" aria-hidden="true">mdi-bell-outline</v-icon></span>
                <span>Studio-Mitteilung</span><span class="notification-time">Jetzt</span>
              </div>
              <h3 :class="{ 'preview-placeholder': !title.trim() }">{{ title.trim() || 'Dein Titel steht hier' }}</h3>
              <p :class="{ 'preview-placeholder': !body.trim() }">{{ body.trim() || 'Schreibe deine Nachricht. Die Vorschau aktualisiert sich beim Tippen.' }}</p>
            </div>
          </div>
          <div class="preview-destination"><v-icon size="20" aria-hidden="true">mdi-storefront-outline</v-icon><span>{{ studioName || 'Noch kein Studio ausgewählt' }}</span></div>
          <p class="preview-footnote">Die Darstellung kann je nach Gerät und Einstellungen abweichen.</p>
        </v-card>
        <div class="writing-tip"><v-icon size="22" aria-hidden="true">mdi-lightbulb-outline</v-icon><p><strong>Ein Gedanke pro Nachricht.</strong> Ein klarer Titel und ein kurzer Text lassen sich unterwegs leichter erfassen.</p></div>
      </aside>
    </div>

    <v-dialog v-model="confirmDialog" max-width="560" :persistent="isSending">
      <v-card dark flat class="push-confirm">
        <div class="confirm-heading"><span class="confirm-icon"><v-icon color="#58d388">mdi-send-outline</v-icon></span><div><span class="confirm-eyebrow">Bereit zum Versand</span><h2>Nachricht senden?</h2></div></div>
        <p class="confirm-intro">Prüfe Studio und Inhalt, bevor du die Push-Nachricht versendest.</p>
        <div class="confirm-audience"><v-icon size="22" aria-hidden="true">mdi-storefront-outline</v-icon><div><strong>{{ studioName }}</strong><span>{{ recipientCount }} {{ recipientCount === 1 ? 'Profil mit registriertem Gerät' : 'Profile mit registriertem Gerät' }}</span></div></div>
        <div class="confirm-message"><h3>{{ title }}</h3><p>{{ body }}</p></div>
        <p class="confirm-note"><v-icon size="18" aria-hidden="true">mdi-information-outline</v-icon>Eine gesendete Nachricht kann nicht zurückgerufen werden.</p>
        <div class="confirm-actions">
          <v-btn text class="push-action" :disabled="isSending" @click="confirmDialog = false">Zurück</v-btn>
          <v-btn color="#007a35" depressed class="push-action" :loading="isSending" :disabled="!isFormValid || loadingCount || recipientCount === 0" @click="sendPush"><v-icon left size="20">mdi-send</v-icon>Jetzt senden</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import MarketingToolHeader from "./MarketingToolHeader.vue"

export default {
  name: "pushDashboard",
  components: { MarketingToolHeader },
  data() {
    return {
      studios: [],
      selectedStudio: null,
      title: "",
      body: "",
      recipientCount: 0,
      loadingCount: false,
      confirmDialog: false,
      isSending: false,
    }
  },
  async created() {
    try {
      let studios = await api.fetchStudios()
      this.studios = studios
    } catch (e) {
      this.$toast.error("Studios konnten nicht geladen werden.")
    }
  },
  computed: {
    studioName() {
      const studio = this.studios.find((item) => item.code === this.selectedStudio)
      return studio ? studio.name : ""
    },
    isFormValid() {
      return this.selectedStudio && this.title.trim().length > 0 && this.body.trim().length > 0
    },
  },
  watch: {
    selectedStudio(newStudioCode) {
      if (newStudioCode) {
        this.fetchRecipientCount(newStudioCode)
      } else {
        this.recipientCount = 0
      }
    },
  },
  methods: {
    async fetchRecipientCount(topic) {
      this.loadingCount = true
      try {
        let response = await api.getTopicAmount(topic)
        this.recipientCount = response.amount || 0
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Berechnen der Empfänger.")
        this.recipientCount = 0
      } finally {
        this.loadingCount = false
      }
    },
    async sendPush() {
      this.isSending = true
      try {
        let notification = {
          topic: this.selectedStudio,
          notification: { title: this.title, body: this.body },
        }
        await api.sendPushToTopic(notification)

        this.$toast.success("Push-Nachricht wurde erfolgreich versendet!")
        this.confirmDialog = false

        // Formular zurücksetzen
        this.title = ""
        this.body = ""
        this.selectedStudio = null
      } catch (e) {
        this.$toast.error("Fehler beim Senden der Push-Nachricht.")
        console.error(e)
      } finally {
        this.isSending = false
      }
    },
  },
}
</script>

<style scoped>
.push-page {
  max-width: 82rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: #f4f4f5;
  font-size: 1rem;
  line-height: 1.6;
}
.push-layout { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); gap: 1.5rem; margin-top: 1.5rem; align-items: start; }
.push-panel.v-card { border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 1.375rem; background: #242727; }
.compose-panel { padding: 1.75rem; }
.section-heading { display: flex; align-items: flex-start; gap: 0.875rem; margin-bottom: 1.5rem; }
.step-number { flex: 0 0 auto; display: grid; place-items: center; width: 2.5rem; height: 2.5rem; border-radius: 0.75rem; background: rgba(0, 150, 64, 0.12); color: #78dda0; font-size: 0.875rem; font-weight: 700; }
.push-page h2, .push-confirm h2 { margin: 0; color: #fff; font-size: 1.25rem; line-height: 1.4; font-weight: 700; }
.section-heading p { margin: 0.25rem 0 0; color: #b8bfbd; font-size: 0.875rem; }
.push-field { border-radius: 0.75rem; }
.push-field ::v-deep .v-input__slot { background: rgba(0, 0, 0, 0.12); }
.push-field ::v-deep input, .push-field ::v-deep textarea, .push-field ::v-deep .v-select__selection { font-size: 1rem; line-height: 1.6; }
.push-field ::v-deep .v-label { font-size: 1rem; }
.push-field ::v-deep .v-label--active { transform: translateY(-1.5rem); background: #242727; padding: 0 0.25rem; font-size: 0.875rem; }
.push-field ::v-deep input::placeholder, .push-field ::v-deep textarea::placeholder { color: #a8b1ad; opacity: 1; }
.push-field ::v-deep .v-select__selection { max-width: 100%; white-space: normal; overflow-wrap: anywhere; }
.audience-status { display: flex; align-items: center; gap: 0.625rem; min-height: 3.5rem; padding: 0.875rem 0; color: #b8bfbd; font-size: 0.875rem; }
.audience-status .v-icon { flex: 0 0 auto; color: #78dda0; }
.audience-status strong { color: #fff; }
.audience-status--empty, .audience-status--empty .v-icon { color: #ebc98d; }
.message-section { margin-top: 0.5rem; padding-top: 1.5rem; border-top: 1px solid rgba(255, 255, 255, 0.09); }
.field-meta { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 0.25rem 1rem; padding: 0.5rem 0.125rem 0; color: #afb8b4; font-size: 0.75rem; }
.message-field { margin-top: 1.5rem; }
.compose-actions { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; margin-top: 1.75rem; padding-top: 1.5rem; border-top: 1px solid rgba(255, 255, 255, 0.09); }
.compose-actions p { flex: 1 1 12rem; margin: 0; color: #b8bfbd; font-size: 0.875rem; }
.push-action.v-btn { min-height: 2.875rem; height: auto; padding: 0.75rem 1.125rem; border-radius: 0.75rem; font-size: 0.875rem; font-weight: 700; letter-spacing: 0; text-transform: none; }
.push-action ::v-deep .v-btn__content { white-space: normal; }
.push-action:focus-visible { outline: 2px solid #78dda0; outline-offset: 3px; }
.preview-column { min-width: 0; }
.preview-panel { padding: 1.5rem; }
.preview-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.preview-badge { display: inline-flex; align-items: center; gap: 0.375rem; border-radius: 2rem; padding: 0.1875rem 0.625rem; background: rgba(0, 150, 64, 0.12); color: #78dda0; font-size: 0.75rem; font-weight: 600; }
.preview-badge::before { content: ''; width: 0.375rem; height: 0.375rem; border-radius: 50%; background: currentColor; }
.preview-intro { margin: 0.5rem 0 1.25rem; color: #b8bfbd; font-size: 0.875rem; }
.preview-stage { padding: 2rem 1.25rem 2.5rem; border-radius: 1rem; background: radial-gradient(ellipse at top right, rgba(76, 144, 114, 0.32), transparent 70%), linear-gradient(145deg, #263e36, #172a23); border: 1px solid rgba(132, 190, 158, 0.12); }
.preview-device-label { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-bottom: 1.5rem; color: #c1d3c9; font-size: 0.75rem; }
.preview-device-label .v-icon { color: inherit; }
.notification-preview { padding: 1.125rem; border-radius: 1.125rem; background: #eff3f0; color: #27332b; box-shadow: 0 0.875rem 2rem rgba(0, 0, 0, 0.2); overflow-wrap: anywhere; }
.notification-meta { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; color: #526157; font-size: 0.75rem; }
.notification-icon { flex: 0 0 auto; display: grid; place-items: center; width: 1.75rem; height: 1.75rem; background: #007a35; border-radius: 0.5rem; }
.notification-time { margin-left: auto; }
.notification-preview h3, .confirm-message h3 { margin: 0 0 0.375rem; font-size: 1rem; line-height: 1.5; font-weight: 700; white-space: pre-wrap; }
.notification-preview p, .confirm-message p { margin: 0; font-size: 1rem; line-height: 1.6; white-space: pre-wrap; }
.notification-preview .preview-placeholder { color: #647168; }
.preview-destination { display: flex; align-items: flex-start; gap: 0.625rem; margin-top: 1.25rem; color: #e0e7e3; font-size: 0.875rem; overflow-wrap: anywhere; }
.preview-destination .v-icon { color: #78dda0; flex: 0 0 auto; }
.preview-footnote { margin: 0.75rem 0 0; color: #aeb8b2; font-size: 0.875rem; line-height: 1.6; }
.writing-tip { display: flex; align-items: flex-start; gap: 0.75rem; padding: 1.25rem 0.5rem; }
.writing-tip .v-icon { color: #78dda0; flex: 0 0 auto; margin-top: 0.125rem; }
.writing-tip p { margin: 0; color: #b8bfbd; font-size: 0.875rem; }
.writing-tip strong { display: block; margin-bottom: 0.25rem; color: #e0e7e3; font-weight: 600; }
.push-confirm.v-card { padding: 1.75rem; background: #242727; border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 1.25rem; color: #f4f4f5; font-size: 1rem; line-height: 1.6; }
.confirm-heading { display: flex; align-items: center; gap: 1rem; }
.confirm-icon { display: grid; place-items: center; width: 3rem; height: 3rem; border-radius: 0.875rem; background: rgba(0, 150, 64, 0.12); flex: 0 0 auto; }
.confirm-eyebrow { display: block; margin-bottom: 0.125rem; color: #78dda0; font-size: 0.75rem; }
.confirm-intro { margin: 1rem 0; color: #b8bfbd; font-size: 1rem; }
.confirm-audience { display: flex; align-items: center; gap: 0.75rem; padding: 1rem 0; overflow-wrap: anywhere; }
.confirm-audience .v-icon { color: #78dda0; }
.confirm-audience strong, .confirm-audience span { display: block; }
.confirm-audience span { color: #b8bfbd; font-size: 0.875rem; }
.confirm-message { margin-top: 0.25rem; padding: 1.125rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.12); overflow-wrap: anywhere; }
.confirm-note { display: flex; align-items: flex-start; gap: 0.5rem; margin: 1rem 0 1.5rem; color: #b8bfbd; font-size: 0.875rem; }
.confirm-note .v-icon { color: inherit; margin-top: 0.125rem; flex: 0 0 auto; }
.confirm-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.75rem; }
@media (max-width: 63.9375rem) {
  .push-layout { grid-template-columns: minmax(0, 1fr); }
  .preview-stage { padding: 1.5rem; }
  .notification-preview { max-width: 26rem; margin: 0 auto; }
}
@media (max-width: 37.4375rem) {
  .push-page { padding: 0.75rem; }
  .push-layout { gap: 1rem; margin-top: 1rem; }
  .compose-panel, .preview-panel, .push-confirm.v-card { padding: 1.125rem; }
  .section-heading { gap: 0.625rem; }
  .preview-stage { padding: 1.25rem 0.75rem; }
  .notification-preview { padding: 0.875rem; }
  .compose-actions { align-items: stretch; }
  .compose-actions .push-action { width: 100%; }
  .confirm-actions .push-action { flex: 1 1 auto; }
}
</style>
